import React, { useCallback, useContext, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "../styles.module.scss";
import localStyles from "./styles.module.scss";
import PagePanel from "app/components/PagePanel";
import Tabs from "app/components/Tabs";
import { useAdminSettingsTabs } from "app/routes/Facility/adminSettingsTabs";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { UserContext } from "app/contexts/UserContext";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import {
  getFacilityLessonRates,
  createFacilityLessonRate,
  updateFacilityLessonRate,
  deleteFacilityLessonRate,
  sortFacilityLessonRates,
  LessonRatePayload,
} from "app/utils/request/scheduling/facilityLessonRates";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import { asyncSchedulingProfileGet, setSchedulingData } from "app/actions/user";
import { Loading } from "app/components/Wrappers";
import { generateCurrencySymbol } from "app/routes/BuildProgram/Pricing/utils";
import BookingOptionsView from "./BookingOptionsView";
import ConflictModal from "app/routes/Scheduling/Pricing/Modals/ConflictModal";
import AddBookingOption from "./Modals/AddBookingOption";
import EditBookingOption from "./Modals/EditBookingOption";
import RemoveBookingOption from "./Modals/RemoveBookingOption";
import {
  getFacilityResources,
  ScheduleResourceResponse,
  ResourceCategoryResponse,
} from "app/utils/request/scheduling/facilityResources";
import { getResourceCategories } from "app/utils/request/scheduling/resourceCategories";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

interface ApiError {
  errors?: Record<string, string[]>;
  status?: number;
}

const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getErrorMessage = (error: ApiError, fallback: string): string => {
  if (error?.errors) {
    const messages = Object.values(error.errors).flat();
    if (messages.length > 0) {
      return capitalizeFirst(messages[0]);
    }
  }
  return fallback;
};

const BookingOptions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useContext(UserContext);
  const currentFacility = useCurrentFacility();
  const tabs = useAdminSettingsTabs("bookingOptions");

  // State for lesson rates and loading
  const [lessonRates, setLessonRates] = useState<LessonRateModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  // Reorder state
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderedRates, setReorderedRates] = useState<LessonRateModel[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Modal states
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isRemoveModalOpen, setRemoveModalOpen] = useState(false);

  // Conflict modal state
  const [isConflictModalOpen, setConflictModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<LessonRateModel | null>(null);
  const [conflictModalText, setConflictModalText] = useState<React.ReactNode>(
    <div>
      {t("You are deactivating the default booking option.")}
      <br />
      {t(
        "Please select an active booking option as your default before saving changes."
      )}
    </div>
  );

  // Facility data
  const facilityId = currentFacility.data?.id;
  const currencySymbol = generateCurrencySymbol(currentFacility.data?.currency);

  const hasAccess =
    user.behavesLikeAdmin &&
    currentFacility.data?.featureFlags?.facilityLevelBookingOptions;

  const showResources =
    user.behavesLikeAdmin &&
    currentFacility.data?.featureFlags?.resourceManagement;

  // Only when the facility has the feature on: the resources tables ship in the
  // same release, and the deployer runs the migration after the deploy.
  const [resources, setResources] = useState<ScheduleResourceResponse[]>([]);
  const [categories, setCategories] = useState<ResourceCategoryResponse[]>([]);

  useEffect(() => {
    // Clear first. A switch to a facility without the feature must not keep the
    // last facility's resources, or the picker would send one of them.
    let ignore = false;
    setResources([]);
    setCategories([]);
    if (!facilityId || !showResources) {
      return undefined;
    }
    getFacilityResources(facilityId)
      .then((resourcesData) => {
        if (!ignore) {
          setResources(resourcesData);
        }
      })
      .catch((error) => {
        if (!ignore) {
          console.error("Error fetching resources:", error);
        }
      });
    getResourceCategories(facilityId)
      .then((categoriesData) => {
        if (!ignore) {
          setCategories(categoriesData);
        }
      })
      .catch((error) => {
        if (!ignore) {
          console.error("Error fetching sections:", error);
        }
      });

    return () => {
      ignore = true;
    };
  }, [facilityId, showResources]);

  // Feature flag for bookings limit field
  const showBookingsLimit =
    currentFacility.data?.featureFlags?.bookingOptionsCapacity === true;

  // Fetch lesson rates when facility is available
  useEffect(() => {
    if (!facilityId) {
      return;
    }
    setIsLoading(true);
    getFacilityLessonRates(facilityId)
      .then((rates) => setLessonRates(rates))
      .catch((error) => console.error("Error fetching lesson rates:", error))
      .finally(() => setIsLoading(false));
  }, [facilityId, refreshTrigger]);

  // Existing titles for validation (exclude current item when editing)
  const existingTitles = lessonRates.map((rate) => rate.title);

  // Handler functions for modals
  const openAddModal = () => {
    setAddModalOpen(true);
  };

  const handleEdit = (item: LessonRateModel) => {
    setCurrentItem(item);
    setEditModalOpen(true);
  };

  const handleRemove = (item: LessonRateModel) => {
    setCurrentItem(item);
    setRemoveModalOpen(true);
  };

  // API handler functions
  const handleAddBooking = async (payload: LessonRatePayload) => {
    if (!facilityId) {
      return;
    }
    try {
      await createFacilityLessonRate(facilityId, payload);
      setRefreshTrigger(!refreshTrigger);
      asyncSchedulingProfileGet().then((facility) => dispatch(setSchedulingData(facility)));
      toast.success(t("Booking Option created successfully."));
      setAddModalOpen(false);
    } catch (error) {
      console.error("Error creating booking option:", error);
      const apiError = error as ApiError;
      if (!apiError?.errors) {
        toast.error(t("Failed to create booking option."));
      }
      throw error;
    }
  };

  const handleEditBooking = async (
    rateId: number,
    payload: Partial<LessonRatePayload>
  ) => {
    if (!facilityId) {
      return;
    }
    try {
      await updateFacilityLessonRate(facilityId, rateId, payload);
      setRefreshTrigger(!refreshTrigger);
      asyncSchedulingProfileGet().then((facility) => dispatch(setSchedulingData(facility)));
      toast.success(t("Booking Option updated successfully."));
      setEditModalOpen(false);
      setCurrentItem(null);
    } catch (error) {
      console.error("Error updating booking option:", error);
      const apiError = error as ApiError;
      if (!apiError?.errors) {
        toast.error(getErrorMessage(apiError, t("Failed to update booking option.")));
      }
      throw error;
    }
  };

  const handleDeleteBooking = async () => {
    if (!facilityId || !currentItem) {
      return;
    }
    try {
      // Note: Backend API handles reassigning default rate when deleting a default rate
      await deleteFacilityLessonRate(facilityId, currentItem.id);
      setRefreshTrigger(!refreshTrigger);
      asyncSchedulingProfileGet().then((facility) => dispatch(setSchedulingData(facility)));
      toast.success(t("Booking Option deleted successfully."));
    } catch (error) {
      console.error("Error deleting booking option:", error);
      toast.error(getErrorMessage(error as ApiError, t("Failed to delete booking option.")));
    } finally {
      // The refusal closes the modal too. Left open, it kept the same currentItem, so the
      // next confirm re-ran the delete that had already been refused and raised the same
      // toast on top of whatever else the admin had just done successfully.
      setRemoveModalOpen(false);
      setCurrentItem(null);
    }
  };

  // Reorder handlers
  const handleEnterReorderMode = () => {
    setReorderedRates([...lessonRates]);
    setIsReorderMode(true);
  };

  const handleOnDragEnd = useCallback((result: any) => {
    if (!result.destination) { return; }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    const items = [...reorderedRates];
    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, reorderedItem);
    setReorderedRates(items);
  }, [reorderedRates]);

  const handleSaveOrder = useCallback(async () => {
    if (isSaving || !facilityId) { return; }
    setIsSaving(true);

    const lessonRatesMap: Record<number, number> = {};
    reorderedRates.forEach((rate, index) => {
      lessonRatesMap[rate.id] = index + 1;
    });

    try {
      await sortFacilityLessonRates(facilityId, lessonRatesMap);
      setLessonRates([...reorderedRates]);
      setIsReorderMode(false);
      toast.success(t("Order saved successfully"));
    } catch (error) {
      toast.error(t("Failed to save order"));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }, [reorderedRates, facilityId, isSaving, t]);

  const handleCancelReorder = () => {
    setIsReorderMode(false);
    setReorderedRates([]);
  };

  // Status change handlers (conflict modal for deactivation warnings)
  const handleChangeStatus = (item: LessonRateModel | null) => {
    if (item !== null) {
      // Deactivating all rates warning
      setCurrentItem(item);
      setConflictModalText(
        <div>
          {t("You deactivated all booking options.")}
          <br />
          {t(
            "To enable athlete bookings, you must have at least one active booking option."
          )}
        </div>
      );
    } else {
      // Deactivating default rate, select new default
      setConflictModalText(
        <div>
          {t("You are deactivating the default booking option.")}
          <br />
          {t(
            "Please select an active booking option as your default before saving changes."
          )}
        </div>
      );
    }
    setConflictModalOpen(true);
  };

  const saveNewStatus = (item: LessonRateModel) => {
    if (!facilityId) {
      return;
    }

    updateFacilityLessonRate(facilityId, item.id, { active: item.active })
      .then(() => {
        setRefreshTrigger(!refreshTrigger);
        asyncSchedulingProfileGet().then((facility) => dispatch(setSchedulingData(facility)));
        toast.success(
          item.active
            ? t("Booking Option activated successfully.")
            : t("Booking Option deactivated successfully.")
        );
      })
      .catch((error) => {
        console.error("Error updating lesson rate:", error);
        toast.error(getErrorMessage(error as ApiError, t("Failed to update booking option.")));
      });
  };

  const onConflictConfirm = () => {
    if (currentItem !== null) {
      currentItem.active = !currentItem.active;
      saveNewStatus(currentItem);
      setCurrentItem(null);
    }
    setConflictModalOpen(false);
  };

  const onConflictDismiss = () => {
    setConflictModalOpen(false);
  };

  // Redirect if not authorized (handles direct URL access)
  if (!currentFacility.pending && !hasAccess) {
    return <Redirect to="/manage_facility" />;
  }

  return (
    <div className="admin-settings-container">
      <div className={styles.title}>
        <h1>{t("Admin Settings")}</h1>
      </div>
      <div>
        <div className={styles.tabs_container}>
          <Tabs tabs={tabs} scrollable />
        </div>
        <PagePanel
          title={t("Booking Options")}
          titleStyles={styles.header}
          headerChildren={
            <div className={localStyles.page_panel}>
              <ButtonVariant
                title={isReorderMode ? t("Save") : t("Reorder")}
                buttonType={isReorderMode ? "primary" : "secondary-outline"}
                onClickFunc={isReorderMode ? handleSaveOrder : handleEnterReorderMode}
              />
              {isReorderMode && (
                <ButtonVariant
                  title={t("Cancel")}
                  buttonType="secondary-outline"
                  onClickFunc={handleCancelReorder}
                />
              )}
              {!isReorderMode && (
                <button
                  type="button"
                  className={localStyles.button2}
                  onClick={openAddModal}
                >
                  <span className={localStyles.fullText}>
                    + {t("Add Booking")}
                  </span>
                  <span className={localStyles.shortText}>+ {t("Add")}</span>
                </button>
              )}
            </div>
          }
        >
          <p className={localStyles.subtitle}>
            {t(
              "Booking Options represent your facility offerings that can be booked by athletes."
            )}
          </p>

          {isLoading ? (
            <Loading isLoading loadType="spinner" />
          ) : lessonRates.length > 0 ? (
            isReorderMode ? (
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="booking-options">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {reorderedRates.map((item, index) => (
                        <Draggable
                          key={item.id}
                          draggableId={String(item.id)}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={localStyles.draggable_item}
                              style={provided.draggableProps.style}
                            >
                              <BookingOptionsView
                                list={[item]}
                                onEdit={handleEdit}
                                onRemove={handleRemove}
                                onChangeStatus={handleChangeStatus}
                                saveNewStatus={saveNewStatus}
                                currencySymbol={currencySymbol}
                                showBookingsLimit={showBookingsLimit}
                                isReorderMode
                                dragHandleProps={provided.dragHandleProps}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <BookingOptionsView
                list={lessonRates}
                onEdit={handleEdit}
                onRemove={handleRemove}
                onChangeStatus={handleChangeStatus}
                saveNewStatus={saveNewStatus}
                currencySymbol={currencySymbol}
                showBookingsLimit={showBookingsLimit}
              />
            )
          ) : (
            <div className={localStyles.no_bookings}>
              <p>{t("You have not created any booking options yet.")}</p>
              <p>
                {t("You must create booking options to enable athlete booking.")}
              </p>
            </div>
          )}
        </PagePanel>
      </div>

      {/* Conflict Modal for deactivation warnings */}
      <ConflictModal
        isOpen={isConflictModalOpen}
        title={t("Deactivate Booking")}
        onConfirm={onConflictConfirm}
        onDismiss={onConflictDismiss}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          {conflictModalText}
        </div>
      </ConflictModal>

      {/* Add Booking Option Modal */}
      <AddBookingOption
        isOpen={isAddModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAddBooking={handleAddBooking}
        existingTitles={existingTitles}
        currencySymbol={currencySymbol}
        showBookingsLimit={showBookingsLimit}
        stripeIntegrationEnabled={currentFacility.data?.stripeIntegrationEnabled}
        resources={resources}
        categories={categories}
        resourcesEnabled={!!showResources}
      />

      {/* Edit Booking Option Modal */}
      <EditBookingOption
        isOpen={isEditModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setCurrentItem(null);
        }}
        item={currentItem}
        onEditBooking={handleEditBooking}
        existingTitles={existingTitles.filter((t) => t !== currentItem?.title)}
        currencySymbol={currencySymbol}
        showBookingsLimit={showBookingsLimit}
        stripeIntegrationEnabled={currentFacility.data?.stripeIntegrationEnabled}
        resources={resources}
        categories={categories}
        resourcesEnabled={!!showResources}
      />

      {/* Remove Booking Option Modal */}
      <RemoveBookingOption
        isOpen={isRemoveModalOpen}
        onClose={() => {
          setRemoveModalOpen(false);
          setCurrentItem(null);
        }}
        onDelete={handleDeleteBooking}
        title={currentItem?.title || ""}
      />
    </div>
  );
};

export default BookingOptions;
